import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { AUTH_EXPIRED_EVENT, ApiError, api } from "../api/client";
import type { User } from "../api/types";

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  error: string;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setStatus("anonymous");
      setError("登录已过期，请重新登录后继续");
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  async function refresh() {
    try {
      const me = await api.auth.me();
      setUser(me);
      setStatus("authenticated");
      setError("");
    } catch (reason) {
      setUser(null);
      setStatus("anonymous");
      if (!(reason instanceof ApiError && reason.status === 401)) {
        setError(reason instanceof Error ? reason.message : "无法连接身份服务");
      }
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function login(username: string, password: string) {
    const me = await api.auth.login(username, password);
    setUser(me);
    setStatus("authenticated");
    setError("");
  }

  async function register(username: string, password: string) {
    const me = await api.auth.register(username, password);
    setUser(me);
    setStatus("authenticated");
    setError("");
  }

  async function logout() {
    try {
      await api.auth.logout();
    } finally {
      setUser(null);
      setStatus("anonymous");
    }
  }

  return (
    <AuthContext.Provider value={{ user, status, error, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
